import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import CallToAction from "../../components/CallToAction";
import IntroBand from "../../components/IntroBand";
import counselorsData from "../../data/placeholder-counselor-data.json";
import CounselorSlide from "../../components/CounselorSlide";
import CounselorSlides from "../../components/CounselorSlides";

const CounselorsSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  gap: 20px;
`;

const ProfileCard = styled.div`
  width: 450px;
  text-align: center;
  margin: 10px 20px;
`;

const ProfileImageWrap = styled.div`
  width: 450px;
  transition: scale 0.3s ease;
  overflow: hidden;
`;

const ProfileImage = styled.img`
  border-radius: 10px;
  transition: filter 0.3s ease, scale 0.3s ease;
  scale: 1;
  object-fit: cover;
  object-position: 0 ${(props) => (props.profilePosition ? `${props.profilePosition}` : '0')};
`;

const ScheduleLink = styled.a`
  color: #5e8a64;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.3;

  position: relative;
  padding-right: 10px;
  cursor: pointer;
  text-decoration: none;
`;

const CounselorExpertise = styled.div`
  font-size: 24px;
  line-height: 1.3;
  font-weight: bold;
  color: #5e8a64;
`;

const CounselorName = styled.div`
  font-size: 24px;
  line-height: 1.3;
  font-weight: bold;
`;

const CounselorTitle = styled.div`
  font-family: Raleway;
  font-size: 20px;
  line-height: 1.3;
  color: black;
`;

const Location = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.27;
`;

const Languages = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.27;
`;

const Tagline = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.27;
`;

const Hero = styled.div`
  z-index: 3;
  position: relative;

  nav {
    z-index: 3;
    position: relative;
  }
`;

const Title = styled.div`
  font-family: Raleway;
  font-size: 40px;
  font-weight: 400;
  line-height: 1.3;
  text-align: left;
  color: black;
  padding-top: 50px;
`;

const ArrowLink = styled.a`
  color: #5e8a64;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.3;

  position: relative;
  padding-right: 40px;
  cursor: pointer;
  text-decoration: none;

  &::after {
    content: url("../images/arrow.svg");
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    transition: right ease 0.4s;
  }

  &:hover {
    opacity: 0.8;
    &::after {
      right: -10px;
    }
  }
  &:active {
    opacity: 0.5;
  }
`;

const ImproveWellbeing = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 1184px;
  padding: 30 30px;
  text-align: left;

  img {
      width: 375px;
      height: 375px;
    }

  .text-block {
    width: 100%;
  }

  .text-block p {
    margin: 30px 30px 30px 0;
  }

  @media screen and (max-width: 970px) {
    flex-direction: column;
    flex-direction: column-reverse;

    .text-block p {
      margin: 30px auto;
    }
  }
`;

const Gap50 = styled.div`
  display: block;
  height: 50px;
`;

const IntroBandAuthor = styled.div`
  font-family: Raleway;
  font-size: 18px;
  font-weight: 400;
  line-height: 26px;
  text-align: center;
  margin-top: 15px;
`;

// Helper function to shuffle an array (Fisher-Yates algorithm)
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Helper function to filter counselors by modality
const filterCounselorsData = (data, filter) => {
  return data.filter((counselor) => 
    counselor.therapies && counselor.therapies.includes(filter)
  );
};

function ModalityDBT() {

  // Use a single Map to store counselor data
  const [counselors, setCounselors] = useState(new Map());

  useEffect(() => {
    // Filter counselors by "Acceptance and Commitment (ACT)"
    const filteredCounselors = filterCounselorsData(counselorsData, "Dialectical Behavior (DBT)");

    // Shuffle the filtered counselors
    const shuffledCounselors = shuffleArray(filteredCounselors);

    // Initialize the Map with filtered counselor data using index as key
    const newCounselors = new Map();
    shuffledCounselors.forEach((counselor, index) => {
      newCounselors.set(index, counselor);
    });


    setCounselors(newCounselors);
  }, []);

  // Create slides from the Map
  const slides = Array.from(counselors.entries()).map(([index, counselor]) => (
    <CounselorSlide key={index} counselor={counselor} header={"DBT Modality Expert"}/>
  ));

  return (
    <div>
      <main>
        <div className="green-bg">
          <Hero>
            <Nav />
            <ImproveWellbeing id="improve-wellbeing">
              <CounselorSlides slides={slides} maxWidth="490px" maxHeight="620px" />            
              <div className="text-block">
                <Title>DBT Modality</Title>
                <p>Dialectical Behavior Therapy (DBT) is a type of therapy designed to help people manage intense emotions and improve relationships. It’s especially helpful for those who struggle with overwhelming feelings, self-harm, or patterns of impulsive behavior.</p>
                <p>DBT combines acceptance strategies—helping you acknowledge and validate your feelings as they are—with change strategies that teach you practical skills to cope more effectively.</p>
                <p>These skills focus on four main areas: mindfulness (being present and aware), distress tolerance (handling crises without making things worse), emotional regulation (understanding and managing emotions), and interpersonal effectiveness (communicating your needs and maintaining healthy relationships).</p>                
                <p>If you're ready to take the next step <ArrowLink href="/contact">Contact Us</ArrowLink></p>
              </div>
            </ImproveWellbeing>
            </Hero>
            <IntroBand>
              <Gap50 />
              "Between stimulus and response there is a space. In that space is
              our power to choose our response. In our response lies our growth
              and our freedom."
              <IntroBandAuthor>- Viktor E. Frankl</IntroBandAuthor>
            </IntroBand>
          </div>
        <CallToAction />
      </main>
      <Footer />
    </div>
  );
}

export default ModalityDBT;
